import type { ProjectsTranslations } from "../../types/projects";

export const projects: ProjectsTranslations = {
  title: "Projects",
  description:
    "Browse each project's folder structure, organise documents, and collaborate with your team.",
  buildingIdRequired: "Building ID is required",
  manageShares: "Manage Shares",
  empty: {
    title: "Create your first project",
    description:
      "Projects keep each building's documents organised in their own folder structure. Create your first project to start uploading and sharing documents with your team.",
    createButton: "Create New Project",
    organiseTitle: "Organise documents by project",
    organiseDesc:
      "Group documents into project-specific folders so everything for a building stays structured and easy to find.",
    collaborateTitle: "Share and collaborate with your team",
    collaborateDesc:
      "Invite your team to a project and manage shared access, so the right people can view and work on the right documents.",
  },
  selector: {
    searchPlaceholder: "Search projects...",
    noProjects: "No projects found.",
    newProject: "New project",
    updatedBy: "Updated by",
    documentsLabel: "documents",
  },
  card: {
    seeAll: "See all",
    showFewer: "Show fewer",
    documents: "documents",
    members: "members",
    issues: "issues",
    actions: "Project actions",
  },
  projectCount: "{count} project(s)",
  chooser: {
    title: "Choose a project",
    subtitle: "Pick a project to continue",
    noAccessTitle: "You're not on a project in this building",
    noAccessBody: "Ask a project owner to add you to a project to see its files.",
  },
  activity: {
    uploadsThisWeek: "{count} uploads this week",
    openIssues: "{count} open issues",
    lastActivity: "Last activity {time}",
    noRecentActivity: "No recent activity",
    unavailable: "Activity unavailable",
  },
  create: {
    button: "Create New Project",
    modalTitle: "New project",
    modalSubtitle: "One development, construction or renovation project",
    modalNameLabel: "Project name",
    modalNamePlaceholder: "Construction design, north-east wing, floor 2",
    modalDescriptionLabel: "Description",
    modalDescriptionPlaceholder: "Briefly describe the project",
    modalOptionalLabel: "Optional",
    modalGuidance:
      "Do not create a separate project for each set of documents. You can split the files into folders inside the project.",
    modalSubmit: "Create project",
    nameLabel: "Project Name",
    namePlaceholder: "Enter project name",
    descriptionLabel: "Description (Optional)",
    descriptionPlaceholder: "Enter project description",
    creating: "Creating...",
    nameRequired: "Project name is required",
    createdSuccess: "Project created! Now select files to upload.",
    success: "Project created successfully",
    createFailed: "Failed to create project",
    authRequired: "Please log in to create projects",
    orgRequired: "Please select an organisation first",
  },
  edit: {
    title: "Edit Project",
    updating: "Updating...",
    submit: "Update Project",
    success: "Project updated successfully",
    failed: "Failed to update project",
    authRequired: "Please log in to update projects",
    orgRequired: "Please select an organisation first",
  },
  status: {
    active: "Active",
    inactive: "Inactive",
    all: "All",
    filterLabel: "Project status",
    markActive: "Mark as active",
    markInactive: "Mark as inactive",
    markedActive: "Project marked as active",
    markedInactive: "Project marked as inactive",
    changeFailed: "Failed to change the project status. Please try again.",
    noPermission: "You don't have permission to change a project's status.",
    noActiveProjects: "No active projects.",
    noInactiveProjects: "No inactive projects.",
  },
  archive: {
    deleteAction: "Delete project",
    deleteTitle: "Delete Project",
    deleteConfirm:
      "Are you sure you want to delete this project? It will be removed from all project lists, but nothing is permanently lost — you can restore it later from the archived view.",
    deleteSubmit: "Delete",
    restore: "Restore",
    showArchived: "Show archived",
    showActive: "Show active",
    noArchived: "No archived projects.",
    restoreTitle: "Restore Project",
    restoreConfirm:
      "Are you sure you want to restore this project? It will be moved back to active projects.",
    archivedSuccess: "Project deleted",
    restoredSuccess: "Project restored",
    archiveFailed: "Failed to delete project. Please try again.",
    restoreFailed: "Failed to restore project. Please try again.",
    defaultProtected: "The default project can't be deleted.",
  },
  selectModal: {
    title: "Select or Create Project",
    selectExisting: "Select Existing",
    createNew: "Create New",
    loading: "Loading projects...",
    noProjects: "No projects found",
    createFirst: "Create your first project to get started",
    selectProject: "Select Project",
    chooseProject: "Choose a project...",
  },
  destination: {
    titleMoveOne: "Move 1 item",
    titleMoveMany: "Move {count} items",
    titleCopyOne: "Copy 1 item",
    titleCopyMany: "Copy {count} items",
    modeLabel: "What should happen to the originals?",
    moveTitle: "Move",
    moveDescription: "They leave their current folder.",
    copyTitle: "Copy",
    copyDescription: "Duplicates are created; the originals stay where they are.",
    destinationHeading: "Destination",
    projectLabel: "Project",
    folderLabel: "Folder",
    noFolders: "This project has no folders yet.",
    folderLoadError: "Could not load folders. Please try again.",
    loadingFolders: "Loading folders\u2026",
    summaryMove: "Will be moved to",
    summaryCopy: "Will be copied to",
    selectFolderHint: "Choose a destination folder.",
    confirmMove: "Move here",
    confirmCopy: "Copy here",
    copying: "Copying\u2026",
    moving: "Moving\u2026",
    current: "(current)",
    currentProjectFallback: "Current project",
  },
  folder: {
    title: "Project folders",
    description:
      "Organise building documents in a project-specific view. Deleting a file here moves it to Trash and it can be restored within 30 days.",
    selectOrganization: "Select an organisation to create folders",
    folderTargetUnconfirmed: "Still opening this folder — try again in a moment.",
    folderLoadFailed: "Couldn't load this project's folders.",
    documentsLoadFailed: "Couldn't load this folder's documents.",
    noFolders: "No folders yet. Create your first folder to get started.",
    loadingDocuments: "Loading documents\u2026",
    upload: "Upload",
    newFolder: "New folder",
    backToProjectRoot: "Back to project root",
    showHiddenFolders: "Show hidden folders: {folders}",
    addFolder: "Add new folder",
    createFolderTitle: "Create New Folder",
    createSubfolderTitle: "Create New Subfolder",
    folderNameLabel: "Folder Name",
    folderNamePlaceholder: "Enter folder name",
    createFolderSubmit: "Create Folder",
    documentMoved: "Document moved",
    alreadyInFolder: "Already in this folder",
    cannotMoveIntoDescendant: "Cannot move a folder into itself or its descendants",
    cannotMoveIntoFolder: "That folder can't take moved items",
    moveDocFailed: "Failed to move document",
    failedOpenContainer: "Failed to open container",
    permanentDeleteTitle: "Permanently Delete Document",
    permanentDeleteConfirm:
      'Are you sure you want to permanently delete "{filename}"? This action cannot be undone.',
    permanentDeleteButton: "Delete",
    permissionDenied: "Permission denied",
    permissionDeniedDesc:
      "Permanent deletion is only available for admins in custom project folders.",
    documentPermanentlyDeleted: "Document permanently deleted",
    documentPermanentlyDeletedDesc: "{filename} has been permanently deleted",
    permanentDeleteFailed: "Permanent delete failed",
    permanentDeleteFailedDesc: "Could not permanently delete the document",
    exportPreparing: 'Preparing export of "{name}"...',
    exportNoDocuments: "No documents to export in this folder.",
    exportDownloadFailed: "Failed to download any files.",
    exportFolderFailed: "Failed to export folder",
    exportSuccess: 'Exported {count} file(s) from "{name}"',
    exportStructureOnly: 'Folder structure for "{name}" downloaded as zip',
    itemsMoved: "Items moved successfully",
    itemsPartiallyMoved: "Moved {moved} item(s), {failed} could not be moved",
    itemsCopied: "Items copied to new project successfully",
    operationFailed: "Operation failed",
    copyToFolderNotSupported:
      "Copy to folder is not supported. Documents can only be in one folder at a time.",
    itemsCopiedToProject: "Items copied to new project successfully",
    folderCopyNotSupported:
      "Folder copying is no longer supported. Please copy documents individually.",
    noItemsSelected: "No items selected",
    failedMoveItems: "Failed to move items",
    failedCopyItems: "Failed to copy items",
    failedUpload: "Failed to process file upload",
    includeSubfolders: "Include subfolders",
  },
  folderPicker: {
    title: "Select Destination Folder",
    rootFolder: "Root Folder",
    noFolders: "No folders available",
    selectFolder: "Select Folder",
  },
};
